/**
 * The answer: one or two sentences in plain language, built from the data (ui/DESIGN.md, "The stage"), plus the
 * insights worth a chip and the kicker route. Pure and deterministic: everything comes from the inputs and `now`.
 * Ports storyOf / insightsOf from components/data/Story.tsx and writes the journey of a single record in B's
 * voice: "SO-757613 is a webshop order of Nastolan Lattiamestarit Oy, placed Mon 09:11. It reached ERP 09:12, the
 * warehouse 09:40 and the data warehouse 09:58 as 3 lines; the delivery was confirmed Tue 07:48 on the second try …"
 *
 * No JSX and no browser globals here, so `node --test src/stage/answer.test.ts` runs it as is.
 */
import dayjs from 'dayjs';
import type { Gap, History, Overview, Preview, RecordKind, RecordList, RecordRow, Records, Run, Runs } from '../lib/api.ts';
import { article, count, duration, humanize } from '../lib/format.ts';
import { late, rhythmOf, type Rhythm } from '../lib/rhythm.ts';
import { stripSystem, systemName } from '../lib/systems.ts';

/**
 * A piece of the sentence: plain text, a hop that links into the map (a node or a flow, at a time), or a live age.
 * `short` is the same hop with fewer words ("the warehouse" for "the warehouse 09:40"), for when the sentence has
 * to make room so that a failure at its end still shows.
 */
export type AnswerPart =
  | string
  | { text: string; short?: string; node?: string; flow?: string; t?: number; failed?: boolean; on?: boolean }
  | { ago: number };

export type InsightTone = 'fail' | 'warn' | 'ok' | 'neutral';

/** Something worth a soft chip under the answer, with what the page should reveal when it is clicked. */
export interface Insight {
  tone: InsightTone;
  text: string;
  /** The runs of this integration. */
  flow?: string;
  /** The records of this data model … */
  node?: string;
  /** … stuck at this step. */
  step?: { cls: string; state: 'missing' | 'pending' };
  /** A value the systems disagree on, or whose update failed (a wall tile id). */
  tile?: string;
}

export interface Answer {
  parts: AnswerPart[];
  insights: Insight[];
  /** System codes in step order, for the kicker: WEB → ERP → WMS → CAR → FIN. */
  route: string[];
  lastSeen: number | null;
  /** What the thing is called ("SO-757613", "Nastolan Lattiamestarit Oy") and what it is ("webshop order", "customer"). */
  title: string;
  kind: string;
}

export interface AnswerInput {
  q: string;
  preview?: Preview | null;
  overview?: Overview | null;
  records?: Records | null;
  runs?: Runs | null;
  /** The list of the thing's own kind of records when the page has one; records.list otherwise. */
  list?: RecordList | null;
  history?: History | null;
  /** Why the newest failed run of an integration failed (its failed step's info), by process name. */
  reasons?: Record<string, string> | null;
  gaps?: Gap[] | null;
  /** The time range of the stage: 24h · 7d · 30d · all · custom. */
  range?: string | null;
  /** Looking at the data as of a moment: nothing newer is told. */
  asOf?: number | null;
  now: number;
}

// ---------------------------------------------------------------- words

/** A data model as people would say it (a copy of noun() in design/primitives.tsx, which is JSX and cannot run under node:test). */
import { noun } from '../lib/nouns.ts';
export { noun };

const leaf = (p: string) => (p.split('/').pop() ?? p).toLowerCase();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "a, b and c" */
export const joinAnd = (xs: string[], word = 'and') => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} ${word} ${xs[xs.length - 1]}`);

/** The systems lib/systems.ts knows by name, as places in a sentence. */
const PLACES: Record<string, string> = {
  WEB: 'the webshop', ERP: 'ERP', WMS: 'the warehouse', CARRIER: 'the carrier', CAR: 'the carrier', FIN: 'finance', CRM: 'CRM', DWH: 'the data warehouse', EDI: 'EDI',
  HR: 'HR', ESG: 'energy reporting', FAC: 'building automation', ECB: 'the ECB',
};

/** A system as a place in a sentence: "ERP", "the webshop", "the data warehouse"; unknown codes stay codes. */
export function place(code: string): string {
  if (PLACES[code]) return PLACES[code];
  const name = systemName(code);
  // systemName() only capitalises a code it does not know: that is no name to put "the" in front of
  if (name === code.charAt(0) + code.slice(1).toLowerCase() || /^[A-Z0-9]+$/.test(name)) return code;
  return `the ${name.toLowerCase()}`;
}

/** "Webshop", "ERP", "Finance": the name as the subject of a clause. */
const who = (code: string) => systemName(code);

/** Up to `max` system names, then "and n others". */
function names(systems: string[], max = 3): string {
  const ns = systems.map(who);
  if (ns.length <= max) return joinAnd(ns);
  return `${ns.slice(0, max - 1).join(', ')} and ${ns.length - max + 1} others`;
}

/** An identifier (SO-757613, 880101, 0015g00005C95675E2) rather than free text (a name, a city): no quotes around it. */
export const isIdentifierLike = (v: string) => /^[\w./:+@-]+$/.test(v) && (/\d/.test(v) || /^[A-Z0-9_-]{2,}$/.test(v));

/** The searched thing at the head of the sentence: an identifier as a hop chip into its own node, free text in quotes. */
const subject = (q: string, node?: string): AnswerPart => (isIdentifierLike(q) ? { text: q, ...(node ? { node } : {}) } : `“${q}”`);

const dayKey = (t: number) => dayjs(t).format('YYYY-MM-DD');

/** "today", "yesterday", "Mon" (within the week), "5 Oct": the day of a time, as seen from now. */
export function dayWord(t: number, now: number): string {
  const d = dayjs(t);
  const today = dayjs(now);
  const days = today.startOf('day').diff(d.startOf('day'), 'day');
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days > 1 && days <= 6) return d.format('ddd');
  return d.format('D MMM');
}

/** Times in a sentence carry their day only when it changes: "Mon 09:12, the warehouse 09:40 … today 11:05". */
class Clock {
  private day: string | null = null;
  private readonly now: number;
  constructor(now: number) {
    this.now = now;
  }
  at(t: number): string {
    const k = dayKey(t);
    const hm = dayjs(t).format('HH:mm');
    if (k === this.day) return hm;
    this.day = k;
    return `${dayWord(t, this.now)} ${hm}`;
  }
}

function humanRange(r: string): string {
  const m = /^(\d+)([mhd])$/.exec(r);
  if (!m) return r;
  const n = Number(m[1]);
  const unit = m[2] === 'd' ? 'day' : m[2] === 'h' ? 'hour' : 'minute';
  return n === 1 ? unit : `${n} ${unit}s`;
}

/** "In the last 30 days", "Up to 5.10. 14:00", "Altogether"; null when the range is unknown. */
function whenPhrase(range: string | null | undefined, asOf: number | null | undefined): string | null {
  if (!range || range === 'custom') return asOf ? `Up to ${dayjs(asOf).format('D.M. HH:mm')}` : null;
  if (asOf) return range === 'all' ? `Up to ${dayjs(asOf).format('D.M. HH:mm')}` : `In the ${humanRange(range)} before ${dayjs(asOf).format('D.M. HH:mm')}`;
  return range === 'all' ? 'Altogether' : `In the last ${humanRange(range)}`;
}

/** "e-invoice operator" from "[FIN] Invoices to e-invoice operator"; null when the name does not say. */
const externalOf = (process: string) => /\bto\s+(.+)$/i.exec(stripSystem(process))?.[1]?.trim() ?? null;

/** "delivery confirmations to ERP": the process name in a sentence, acronyms kept. */
const procName = (process: string) => stripSystem(process).split(/\s+/).map((w) => (/^[A-Z0-9]{2,}$/.test(w) ? w : w.toLowerCase())).join(' ');

// ---------------------------------------------------------------- what the thing is

/** Types the cloud gives one record: everything else is a party, a place, a product or a plain value. */
const SINGLE_TYPES = new Set(['Order', 'Invoice', 'Shipment']);

/** The data models whose key is the searched identifier: the thing's own kind(s). */
function ownKinds(kinds: RecordKind[], preview: Preview | null | undefined, overview: Overview | null | undefined): RecordKind[] {
  const ids = (preview?.identities ?? overview?.identifiedBy ?? []).filter((i) => i.searched);
  const fields = new Set(ids.flatMap((i) => i.fields.map(leaf)));
  if (fields.size === 0) return [];
  return kinds.filter((k) => k.key.some((p) => fields.has(leaf(p))));
}

/** The row of the thing itself in a list: its key or one of its values is what was searched. */
function rowOf(list: RecordList | null | undefined, q: string): RecordRow | null {
  if (!list) return null;
  const v = q.trim().replace(/^"|"$/g, '').toLowerCase();
  return list.rows.find((r) => r.key.toLowerCase() === v) ?? list.rows.find((r) => Object.values(r.values).some((x) => x.toLowerCase() === v)) ?? null;
}

interface Hop { cls: string; system: string; t: number | null; state: 'seen' | 'missing' | 'pending' | 'none'; before: boolean }

/** The steps of a record as hops: the kinds it reached and when, from the row's first-seen per step. */
function hopsOf(list: RecordList, row: RecordRow): Hop[] {
  return list.steps.map((st, i) => {
    const cell = row.steps[i];
    const t = typeof cell === 'number' && cell > 0 ? cell : null;
    const state: Hop['state'] = cell === 'missing' ? 'missing' : cell === 'pending' ? 'pending' : t !== null || cell === 0 ? 'seen' : 'none';
    return { cls: st.cls, system: st.system, t, state, before: st.typicalMs !== null && st.typicalMs < 0 };
  });
}

/** The status a run ends up with (as in components/data/Runs.tsx). */
const runFailed = (r: Run) => r.failed > 0 || !!r.statuses.FAILED;

interface ProcessStory { process: string; system: string | null; runs: Run[]; failed: Run[]; healed: { after: number; at: number } | null; stillFailing: boolean }

/** Per integration: its runs in time order, which failed, and whether a later run healed it. */
function processStories(runs: Runs | null | undefined): ProcessStory[] {
  const by = new Map<string, Run[]>();
  for (const r of runs?.list ?? []) {
    if (!r.process) continue;
    by.set(r.process, [...(by.get(r.process) ?? []), r]);
  }
  const out: ProcessStory[] = [];
  for (const [process, list] of by) {
    const sorted = [...list].sort((a, b) => a.first - b.first);
    const failed = sorted.filter(runFailed);
    let healed: ProcessStory['healed'] = null;
    for (const f of failed) {
      const next = sorted.find((r) => r.first > f.first && !runFailed(r));
      if (next) {
        healed = { after: next.first - f.first, at: next.first };
        break;
      }
    }
    const last = sorted[sorted.length - 1];
    out.push({ process, system: last.system, runs: sorted, failed, healed, stillFailing: failed.length > 0 && runFailed(last) });
  }
  return out.sort((a, b) => b.failed.length - a.failed.length || a.process.localeCompare(b.process));
}

// ---------------------------------------------------------------- one record

/**
 * What a hop says: "reached the warehouse 09:40" (plain copies) or a clause of its own ("the delivery was confirmed
 * 07:48"). With an empty `time` it is the short form ("the warehouse", "the delivery was confirmed").
 */
function hopPhrase(h: Hop, time: string, docs: number | undefined): { reached: boolean; text: string } {
  const n = noun(h.cls);
  const head = n.split(' ').pop() ?? n;
  const at = (s: string) => (time ? `${s} ${time}` : s);
  switch (head) {
    case 'line': case 'row': case 'entry':
      return { reached: true, text: `${at(place(h.system))}${docs && docs > 1 ? ` as ${docs} ${plural(docs, head)}` : ''}` };
    case 'delivery': case 'confirmation': case 'slip':
      return { reached: false, text: at(`the ${n} was confirmed`) };
    case 'shipment': case 'dispatch':
      return { reached: false, text: at('it was shipped') };
    case 'tracking': case 'event':
      return { reached: false, text: at(`${place(h.system)} tracked it`) };
    case 'fulfillment': case 'fulfilment': case 'notification': case 'status': case 'update':
      return { reached: false, text: at(`${place(h.system)} was told`) };
    case 'invoice':
      return { reached: false, text: at('it was invoiced') };
    case 'payment':
      return { reached: false, text: at('it was paid') };
    case 'response': case 'acknowledgement': case 'ack':
      return { reached: false, text: at(`the ${n} went out`) };
    case 'cancellation':
      return { reached: false, text: at('it was cancelled') };
    default:
      return { reached: true, text: at(place(h.system)) };
  }
}

/**
 * How often the record's own documents come, when they keep coming (a meter's hourly readings, a customer synced
 * daily): one integration bringing them again and again. An order's three documents from three integrations
 * (created, read for the warehouse, invoiced) are a journey, not a rhythm.
 */
function ownRhythm(s: Single, stories: ProcessStory[]): Rhythm | null {
  if (s.row.messages < 3) return null;
  const own = stories.filter((p) => p.runs.some((r) => r.classes.includes(s.own.cls)));
  if (own.length !== 1 || own[0].runs.length < 3) return null;
  const r = rhythmOf(own[0].runs.map((r) => r.first));
  return r.scheduled ? r : null;
}

interface Single { own: RecordKind; list: RecordList; row: RecordRow }

function singleAnswer(input: AnswerInput, s: Single): Answer {
  const { preview, overview, runs, asOf, now } = input;
  const clock = new Clock(now);
  const docs = new Map((preview?.classes ?? []).map((c) => [c.cls, c.total]));
  const all = hopsOf(s.list, s.row).filter((h) => h.t === null || !asOf || h.t <= asOf);
  const head = (cls: string) => noun(cls).split(' ').pop() ?? '';
  // Where it came from: an earlier step that is the same thing (a webshop order becomes the sales order) or what an
  // aggregate is made of (an invoice of orders, a consumption of readings). Anything else that happens to come
  // earlier in the data (a line, a delivery of another order in the window) is just another hop
  const aggregate = /^(invoice|consumption|statement|report|summary|batch)$/.test(head(s.own.cls));
  const candidate = all.find((h) => h.before && h.state === 'seen' && !/^(line|row|entry)$/.test(head(h.cls)));
  const sameThing = !!candidate && head(candidate.cls) === head(s.own.cls);
  const origin = candidate && (sameThing || aggregate) ? candidate : undefined;
  const hops = all.map((h) => ({ ...h, before: !!origin && h.cls === origin.cls }));
  const customer = nameOf(overview);
  const stories = processStories(runs).filter((p) => !asOf || p.runs.some((r) => r.first <= asOf));
  const placed = origin?.t ?? s.row.first;

  const parts: AnswerPart[] = [];
  const kind = origin && sameThing ? [originWord(origin.system), noun(origin.cls)].filter(Boolean).join(' ') : noun(s.own.cls);
  // Orders are placed; a reading, a customer or an employee is first seen
  const placedWord = /\b(order|invoice|shipment)\b/.test(kind) ? 'placed' : 'first seen';
  parts.push({ text: s.row.key, node: s.own.cls, t: s.row.first });
  parts.push(` is ${article(kind)} `);
  if (origin && sameThing) parts.push({ text: kind, node: origin.cls, t: origin.t ?? undefined });
  else parts.push(kind);
  if (customer) parts.push(' of ', { text: customer.value, node: customer.cls ?? undefined });
  // A record whose documents keep coming says how often: "that comes hourly at :02, 358 times since 23 Sep 00:02"
  const rhythm = ownRhythm(s, stories);
  if (origin && !sameThing) parts.push(', made of ', { text: `${noun(origin.cls, 2)} placed ${clock.at(placed)}`, node: origin.cls, t: origin.t ?? undefined }, '.');
  else if (rhythm) parts.push(` that comes ${rhythm.label}, ${count(s.row.messages)} times since ${clock.at(placed)}.`);
  else parts.push(`, ${placedWord} ${clock.at(placed)}.`);

  // The journey after the origin: the own kind first (where the record lives), then every step it reached, in time order
  const journey: { hop: Hop; retried: number }[] = [];
  const seen = hops.filter((h) => !h.before && h.state === 'seen' && h.t !== null).sort((a, b) => a.t! - b.t!);
  // "on the second try": only when every run of the integration but the last failed, so they are plausibly this record's
  const retries = new Map<string, number>();
  for (const p of stories) {
    if (!p.healed || p.runs.length > p.failed.length + 1) continue;
    for (const cls of new Set(p.runs.flatMap((r) => r.classes))) if (cls !== s.own.cls) retries.set(cls, p.failed.length + 1);
  }
  if (origin) journey.push({ hop: { cls: s.own.cls, system: s.own.system, t: s.row.first, state: 'seen', before: false }, retried: 0 });
  for (const h of seen) journey.push({ hop: h, retried: retries.get(h.cls) ?? 0 });
  // In the order things happened: an invoice made of orders is invoiced after the orders' own hops
  journey.sort((a, b) => a.hop.t! - b.hop.t!);

  if (journey.length > 0) {
    // Consecutive plain copies fold into one "it reached A, B and C"; the rest are clauses of their own
    const segments: AnswerPart[][] = [];
    let reaching: AnswerPart[][] | null = null;
    const flush = () => {
      if (!reaching) return;
      const seg: AnswerPart[] = ['it reached '];
      reaching.forEach((hop, i) => {
        if (i > 0) seg.push(i === reaching!.length - 1 ? ' and ' : ', ');
        seg.push(...hop);
      });
      segments.push(seg);
      reaching = null;
    };
    for (const { hop, retried } of journey) {
      const time = clock.at(hop.t!);
      const ph = hopPhrase(hop, time, docs.get(hop.cls));
      const text = retried > 1 ? `${ph.text} on the ${ordinal(retried)} try` : ph.text;
      const link: AnswerPart = { text, short: hopPhrase(hop, '', undefined).text, node: hop.cls, t: hop.t! };
      if (ph.reached) (reaching ??= []).push([link]);
      else {
        flush();
        segments.push([link]);
      }
    }
    flush();
    parts.push(' ');
    segments.forEach((seg, i) => {
      if (i === 1) parts.push('; ');
      else if (i > 1) parts.push(i === segments.length - 1 ? ' and ' : ', ');
      const first = seg[0];
      // The first clause starts the sentence: "It reached …", "The delivery was confirmed …"
      if (i === 0) {
        if (typeof first === 'string') seg[0] = cap(first);
        else if ('text' in first) seg[0] = { ...first, text: cap(first.text), ...(first.short ? { short: cap(first.short) } : {}) };
      }
      parts.push(...seg);
    });
    parts.push('.');
  }

  // What went wrong, or is still to come
  const trouble: AnswerPart[][] = [];
  for (const p of stories.filter((x) => x.stillFailing)) {
    const run = p.failed[p.failed.length - 1];
    const from = run.classes.find((c) => c !== s.own.cls) ?? run.classes[0] ?? s.own.cls;
    const target = externalOf(p.process);
    const other = run.classes.map((c) => s.list.steps.find((st) => st.cls === c)?.system ?? (c === s.own.cls ? s.own.system : null)).find((sys) => sys && sys !== p.system);
    const dest = target ? (/^the\b/i.test(target) ? target : `the ${target}`) : other ? place(other) : `${who(p.system ?? from.slice(0, 3).toUpperCase())}`;
    const why = reasonOf(input, p.process);
    trouble.push([`the ${noun(from)} left ${place(p.system ?? s.own.system)} ${clock.at(run.first)} but `, { text: `never reached ${dest}${why ? ` (${why})` : ''}`, flow: p.process, t: run.first, failed: true }]);
  }
  const missing = hops.filter((h) => h.state === 'missing');
  if (missing.length) {
    const seg: AnswerPart[] = ['it never got '];
    missing.forEach((h, i) => {
      if (i > 0) seg.push(i === missing.length - 1 ? ' or ' : ', ');
      seg.push({ text: `${article(noun(h.cls))} ${noun(h.cls)}`, node: h.cls, failed: true });
    });
    trouble.push(seg);
  }
  const pending = hops.filter((h) => h.state === 'pending');
  if (pending.length) {
    trouble.push([{ text: `the ${joinAnd(pending.map((h) => noun(h.cls)))} ${pending.length === 1 ? 'is' : 'are'} not due yet`, node: pending[0].cls }]);
  }
  if (trouble.length) {
    parts.push(' ');
    trouble.forEach((seg, i) => {
      if (i > 0) parts.push(i === trouble.length - 1 ? ' and ' : ', ');
      const first = seg[0];
      if (i === 0) {
        if (typeof first === 'string') seg[0] = cap(first);
        else if ('text' in first) seg[0] = { ...first, text: cap(first.text) };
      }
      parts.push(...seg);
    });
    parts.push('.');
  }

  const times = [s.row.first, s.row.last, ...hops.map((h) => h.t ?? 0), ...stories.flatMap((p) => p.runs.map((r) => r.last)), preview?.last ?? 0]
    .filter((t): t is number => !!t && (!asOf || t <= asOf));
  // The kicker says when it was last seen: one fact, one place
  const lastSeen = times.length ? Math.max(...times) : null;

  const route = dedupe([
    ...(origin ? [origin.system] : []), s.own.system,
    ...hops.filter((h) => !h.before).sort((a, b) => (a.t ?? Infinity) - (b.t ?? Infinity)).map((h) => h.system),
  ]);
  return { parts, insights: insightsOf(input, { single: true, list: s.list, type: preview?.type ?? overview?.type ?? null, own: new Set([s.own.cls]) }), route, lastSeen, title: s.row.key, kind };
}

/** "webshop" for WEB, "EDI" for EDI: the origin as an adjective; nothing for a system only known by its code. */
function originWord(code: string): string {
  const name = systemName(code);
  if (name === code.charAt(0) + code.slice(1).toLowerCase()) return '';
  return /^[A-Z0-9]+$/.test(name) ? name : name.toLowerCase();
}

const ORDINALS = ['first', 'second', 'third', 'fourth', 'fifth'];
const ordinal = (n: number) => ORDINALS[n - 1] ?? `${n}th`;

const dedupe = (xs: string[]) => [...new Set(xs)];

/** The customer's name among what was found (the "name" family of the wall or the attributes), with the data model it came from. */
function nameOf(overview: Overview | null | undefined): { value: string; cls: string | null } | null {
  const tile = overview?.wall.find((t) => t.family === 'name' && t.kind === 'value' && !t.anchor);
  if (tile) return { value: tile.value, cls: tile.sources.find((s) => /customer|account|party/i.test(s.cls))?.cls ?? tile.classes[0] ?? null };
  const attr = overview?.attributes.find((a) => a.family === 'name' && a.consensus);
  return attr ? { value: attr.consensus, cls: attr.cells[0]?.sources[0]?.cls ?? null } : null;
}

// ---------------------------------------------------------------- many records (a customer, a product, a value)

function manyAnswer(input: AnswerInput, kinds: RecordKind[], own: RecordKind[]): Answer {
  const { q, preview, overview, range, asOf } = input;
  const type = preview?.type ?? overview?.type ?? 'Value';
  const title = preview?.title ?? overview?.title ?? q;
  const total = preview?.total ?? overview?.total ?? 0;
  const parts: AnswerPart[] = [];
  const kind = type === 'Value' ? 'value' : type.toLowerCase();
  const list = input.list ?? input.records?.list ?? null;
  const before = (typicalMs: number | null) => typicalMs !== null && typicalMs < 0;
  // The systems the map draws (the same data models the stage's kinds come from) and the kicker's route through them
  const mapKinds = overview?.kinds?.length ? overview.kinds : kinds;
  const systems = dedupe(mapKinds.length ? mapKinds.map((k) => k.system) : (preview?.systems ?? overview?.systems ?? []).map((s) => s.code));
  const route = dedupe(list
    ? [...list.steps.filter((st) => before(st.typicalMs)).map((st) => st.system), list.system, ...list.steps.filter((st) => !before(st.typicalMs)).map((st) => st.system)]
    : systems);
  // Where the thing's own record lives: the kinds keyed by its identifier, and every kind that is the same noun (a
  // customer in the webshop, ERP and CRM)
  const ownSystems = dedupe([...own, ...kinds.filter((k) => noun(k.cls) === kind)].map((k) => k.system));
  const where = (xs: string[]) => `${xs.length} ${plural(xs.length, 'system')}${xs.length <= 4 ? ` (${names(xs, 4)})` : ''}`;
  const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
  // A party whose own record lives in fewer systems than its documents run through: two facts, never one wrong count
  const twoFacts = ownSystems.length > 0 && list && !own.some((k) => k.cls === list.cls) && !sameSet(ownSystems, systems)
    ? `; its ${kind} record lives in ${where(ownSystems)}; its ${noun(list.cls, 2)} run through ${route.length}`
    : null;
  const knownTo = systems.length ? `known to ${where(systems)}` : 'not known to any system yet';

  if (type === 'Value' || type === 'Product' || /^(Postcode|City|Street|Country)$/.test(type)) {
    parts.push(subject(q, own[0]?.cls), ' is ', type === 'Value' ? 'in' : `${article(kind)} ${kind} in`,
      ` ${count(total)} ${plural(total, 'document')}`, systems.length ? ` across ${where(systems)}.` : '.');
  } else if (title && title !== q) {
    parts.push(subject(q, own[0]?.cls), ' is ', title, twoFacts ? `${twoFacts}.` : `, ${knownTo}.`);
  } else {
    parts.push(subject(q, own[0]?.cls), ` is ${article(kind)} ${kind}`, twoFacts ? `${twoFacts}.` : ` ${knownTo}.`);
  }

  // What it has: the kinds of records found in the order of their journey (the listed kind, then its steps), the
  // rest by count; the thing's own kind, the origin of a listed kind (a webshop order is the sales order) and
  // the lines of something else come last or not at all
  const journey = list ? [list.cls, ...list.steps.filter((st) => !before(st.typicalMs)).map((st) => st.cls)] : [];
  const origins = new Set(list?.steps.filter((st) => before(st.typicalMs)).map((st) => st.cls));
  const detail = (cls: string) => /\b(line|row|entry)$/.test(noun(cls));
  const rank = (k: RecordKind) => (journey.includes(k.cls) ? journey.indexOf(k.cls) : 100) + (detail(k.cls) ? 1000 : 0);
  const has = kinds
    .filter((k) => !own.includes(k) && noun(k.cls) !== kind && !origins.has(k.cls))
    .sort((a, b) => rank(a) - rank(b) || b.records - a.records);
  if (has.length) {
    const when = whenPhrase(range, asOf);
    parts.push(when ? ` ${when}: ` : ' It has ');
    const top = has.slice(0, 5);
    top.forEach((k, i) => {
      if (i > 0) parts.push(i === top.length - 1 && has.length <= 5 ? ' and ' : ', ');
      parts.push({ text: `${count(k.records)}${k.more ? '+' : ''} ${noun(k.cls, k.records)}`, node: k.cls });
    });
    if (has.length > 5) parts.push(` and ${has.length - 5} more ${plural(has.length - 5, 'kind')} of records`);
    parts.push('.');
  }

  const lastSeen = [preview?.last ?? 0, overview?.window.last ?? 0].filter((t) => t && (!asOf || t <= asOf)).reduce((a, b) => Math.max(a, b), 0) || null;
  return { parts, insights: insightsOf(input, { single: false, list, type, own: new Set(own.map((k) => k.cls)) }), route, lastSeen, title, kind };
}

/** Before anything but the preview is in: what it is and who knows it, so the card never waits. */
function skeletonAnswer(input: AnswerInput): Answer {
  const { q, preview, asOf } = input;
  const parts: AnswerPart[] = [];
  const type = preview?.type ?? 'Value';
  const kind = type === 'Value' ? 'value' : type.toLowerCase();
  const title = preview?.title ?? q;
  const systems = (preview?.systems ?? []).map((s) => s.code);
  if (!preview) parts.push('Looking for ', subject(q), '…');
  else if (preview.total === 0) {
    const when = input.range && /^\d+[mhd]$/.test(input.range) ? `in the last ${humanRange(input.range)}` : input.range === 'all' ? 'ever' : 'in this time';
    parts.push(`Nothing has been seen of “${q}” ${when}.`);
  }
  else {
    const where = systems.length ? ` of ${systems.length} ${plural(systems.length, 'system')} (${names(systems, 4)})` : '';
    if (type === 'Value') parts.push(subject(q), ` is in ${count(preview.total)} ${plural(preview.total, 'document')}${where}.`);
    else if (title !== q) parts.push(subject(q), ` is ${title}, ${article(kind)} ${kind} known to ${systems.length} ${plural(systems.length, 'system')}${systems.length <= 4 && systems.length ? ` (${names(systems, 4)})` : ''}.`);
    else parts.push(subject(q), ` is ${article(kind)} ${kind} with ${count(preview.total)} ${plural(preview.total, 'document')}${where}.`);
    if (preview.first && preview.last) {
      const clock = new Clock(input.now);
      parts.push(preview.first === preview.last ? ` Seen ${clock.at(preview.first)}.` : ` First seen ${clock.at(preview.first)}, last ${clock.at(preview.last)}.`);
    }
  }
  const lastSeen = preview?.last && (!asOf || preview.last <= asOf) ? preview.last : null;
  return { parts, insights: insightsOf(input, { single: false, list: null, type, own: new Set() }), route: systems, lastSeen, title, kind };
}

// ---------------------------------------------------------------- insights

const FAILED_INFO = /^.*?failed:\s*/i;
/** A disagreement is told in full up to this; past it the odd one out is named without its value. */
const CONFLICT_MAX = 110;

/** What a failure's reason means for the business, when the reason says a known thing at length. */
const CONSEQUENCES: [RegExp, string][] = [
  [/approv/i, 'needs approval'],
  [/\b401\b|unauthori[sz]ed|token (has )?expired|invalid token|\b403\b|forbidden/i, 'login refused'],
  [/\b409\b|locked by|conflict/i, 'record locked'],
  [/\b404\b|not found|unknown (customer|item|product|account)/i, 'not found'],
  [/\b50[23]\b|unavailable|timed? ?out|unreachable|connection refused/i, 'other end down'],
  [/duplicate|already exists/i, 'already exists'],
  [/validation|invalid|required field|missing field|schema/i, 'rejected as invalid'],
];
/** A reason this short is told as it is ("Maventa 401, token expired"). */
const SHORT_REASON = 44;
/** The words HTTP puts after a status code say nothing the code does not. */
const HTTP_WORDS = /\b(\d{3})\s+(unauthori[sz]ed|forbidden|not found|conflict|bad request|internal server error|service unavailable|gateway time-?out|bad gateway)\b/gi;

/** "Maventa 401 Unauthorized (token expired)" → "Maventa 401, token expired": the reason as a short aside. */
export function tidyReason(info: string): string {
  return info.replace(FAILED_INFO, '').trim().replace(HTTP_WORDS, '$1').replace(/\s*\(([^()]*)\)\s*$/, ', $1').replace(/\.$/, '').trim();
}

/**
 * The business consequence of a failure's reason, short: the reason itself when it is short, else what it
 * amounts to ("needs approval in ERP"), else its first clause. `system` names where the consequence applies.
 */
export function consequence(info: string, system?: string | null): string {
  const reason = tidyReason(info);
  if (reason.length <= SHORT_REASON) return reason;
  const where = system ? ` in ${who(system)}` : '';
  for (const [re, text] of CONSEQUENCES) if (re.test(reason)) return `${text}${where}`;
  return reason.split(/\s+[-–—:]\s+|:\s+|\s+\(/)[0].trim();
}

/** Why an integration's newest run failed: the failed step's info the page loaded, else a failed event of the history. */
function reasonOf(input: AnswerInput, process: string): string | null {
  const info = input.reasons?.[process]
    ?? (input.history?.events ?? []).filter((e) => !input.asOf || e.at <= input.asOf).find((e) => e.process === process && e.status === 'FAILED' && e.info)?.info
    ?? null;
  return info ? consequence(info) : null;
}

/** Kinds of searched things whose attributes are their own: a disagreement between systems is about them. */
const PARTY_TYPES = new Set(['Customer', 'Supplier', 'Vendor', 'Employee', 'Account', 'Order', 'Invoice', 'Shipment', 'Meter']);

/** What needs attention: failed integrations, records stuck, values the systems disagree on, updates that failed, silence, retries. */
export function insightsOf(input: AnswerInput, ctx: { single: boolean; list: RecordList | null; type: string | null; own: Set<string> }): Insight[] {
  const { overview, gaps, now, asOf } = input;
  const fail: Insight[] = [];
  const warn: Insight[] = [];
  const ok: Insight[] = [];
  const at = asOf ?? now;
  // As of a moment, only what had happened by then
  const runs = asOf && input.runs ? { ...input.runs, list: input.runs.list.filter((r) => r.first <= asOf) } : input.runs;

  // Failed runs, per integration, with the reason ("7 of 80 e-invoice runs failed · Maventa 401, token expired"); one
  // that healed on a retry is good news
  for (const p of processStories(runs).filter((x) => x.failed.length > 0).slice(0, 3)) {
    const why = reasonOf(input, p.process);
    if (p.stillFailing) {
      const n = p.failed.length;
      const last = p.failed[p.failed.length - 1];
      const text = p.runs.length === 1 ? `${procName(p.process)} failed ${dayWord(last.first, now)} ${dayjs(last.first).format('HH:mm')}`
        : p.runs.length > n ? `${count(n)} of ${count(p.runs.length)} ${procName(p.process)} runs failed` : `${count(n)} ${procName(p.process)} ${plural(n, 'run')} failed`;
      fail.push({ tone: 'fail', text: why ? `${text} · ${why}` : text, flow: p.process });
    } else if (p.healed) {
      ok.push({ tone: 'ok', text: `${procName(p.process)} · retried after ${duration(p.healed.after)}`, flow: p.process });
    }
  }

  // Records whose next step never came (the list's own tally when there is one, the gaps check otherwise)
  if (!ctx.single && ctx.list) {
    const list = ctx.list;
    list.steps.forEach((st, i) => {
      const n = list.rows.filter((r) => r.steps[i] === 'missing').length;
      if (n > 0) fail.push({ tone: 'fail', text: `${count(n)} ${noun(list.cls, n)} never got ${article(noun(st.cls))} ${noun(st.cls)}`, node: list.cls, step: { cls: st.cls, state: 'missing' } });
    });
  } else if (!ctx.single && gaps?.length) {
    const stuck = new Set(gaps.flatMap((g) => g.missing.map((m) => m.value))).size;
    if (stuck > 0) fail.push({ tone: 'fail', text: `${count(stuck)} ${plural(stuck, 'record')} never got their next step`, node: gaps[0].from.cls });
  }

  // Values the systems disagree on: only about the searched thing itself (a party or one record, or an attribute of
  // its own kind) — on a product's stage the business ids of the customers who bought it are different customers, not
  // a conflict. The odd one out first; long values are left to the tile ("name differs · Webshop vs ERP and 2 others")
  const about = (t: { classes: string[] }) => ctx.single || PARTY_TYPES.has(ctx.type ?? '') || (t.classes.length > 0 && t.classes.every((c) => ctx.own.has(c)));
  for (const t of (overview?.wall ?? []).filter((x) => x.state === 'conflict' && x.kind === 'value' && x.variants.length > 1 && about(x)).slice(0, 2)) {
    const [a, ...rest] = [...t.variants].sort((x, y) => y.systems.length - x.systems.length);
    const b = rest[0];
    const label = humanize(t.label).toLowerCase();
    const full = `${label} differs · ${names(b.systems)} ${plural(b.systems.length, 'says', 'say')} ${b.value}, ${names(a.systems, 2)} ${a.value}`;
    warn.push({ tone: 'warn', tile: t.id, text: full.length <= CONFLICT_MAX ? full : `${label} differs · ${names(b.systems)} vs ${names(a.systems, 2)}` });
  }

  // An update of a value that failed (the newest document of a source is a failure): what it means, and where
  const seenFailures = new Set<string>();
  for (const t of (overview?.wall ?? []).filter((x) => x.sources.some((s) => s.failure) && about(x)).slice(0, 2)) {
    const src = t.sources.find((s) => s.failure)!;
    const f = src.failure!;
    if (seenFailures.has(f.info)) continue;
    seenFailures.add(f.info);
    fail.push({ tone: 'fail', tile: t.id, text: `${humanize(t.label).toLowerCase()} update failed · ${consequence(f.info, src.system)}` });
  }

  // An integration on a schedule that has gone quiet
  for (const p of processStories(runs)) {
    if (p.runs.length < 3) continue;
    const r = rhythmOf(p.runs.map((x) => x.first));
    if (r.scheduled && late(r, at)) warn.push({ tone: 'warn', flow: p.process, text: `${procName(p.process)} has not run for ${duration(at - (r.last ?? at))} · usually ${r.label}` });
  }

  return [...fail, ...warn, ...ok].slice(0, 5);
}

// ---------------------------------------------------------------- the answer

export function answerOf(input: AnswerInput): Answer {
  const { q, preview, overview, records } = input;
  const kinds = records?.kinds ?? [];
  const own = ownKinds(kinds, preview, overview);
  const type = preview?.type ?? overview?.type ?? null;
  const lists = [input.list, records?.list].filter((l): l is RecordList => !!l);
  const ownList = lists.find((l) => own.some((k) => k.cls === l.cls)) ?? (own.length === 0 && SINGLE_TYPES.has(type ?? '') ? lists[0] : undefined);
  const row = rowOf(ownList, q);
  const singleByType = type !== null && SINGLE_TYPES.has(type);
  if (ownList && row && (singleByType || type === 'Value')) {
    const kind = own.find((k) => k.cls === ownList.cls) ?? kinds.find((k) => k.cls === ownList.cls) ?? { cls: ownList.cls, system: ownList.system, key: ownList.key, label: ownList.label, records: ownList.records, messages: ownList.messages };
    return singleAnswer(input, { own: kind, list: ownList, row });
  }
  // Nothing found: say so, whatever else came back empty
  if (kinds.length === 0 && (preview?.total ?? overview?.total ?? 0) === 0 && (preview || overview)) return skeletonAnswer(input);
  if (kinds.length > 0 || overview) return manyAnswer(input, kinds, own);
  return skeletonAnswer(input);
}

/** The sentence as plain text (for tests, titles and the document title). */
export function answerText(parts: AnswerPart[]): string {
  return parts.map((p) => (typeof p === 'string' ? p : 'ago' in p ? '…' : p.text)).join('');
}
